import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnInit,
  Renderer2,
  Output,
  EventEmitter,
  ElementRef,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  AVAILABLEDOCUMENTTYPES,
  PAYMENTMETHODS,
} from "../../../document/document.data";

@Component({
  selector: "app-invoice-template2",
  templateUrl: "./invoice-template2.component.html",
  styleUrls: ["./invoice-template2.component.scss"],
})
export class InvoiceTemplate2Component
  implements OnChanges, AfterViewInit, OnInit
{
  @Input() invoiceData: any;
  @Input() lang: "en" | "ar" | "he" = "en";
  @Input() showInvoiceStop: boolean = false;
  @Input() invoiceId: number | null = null;
  @Input() userOptions: boolean = false;
  @Input() pdfLink: string | null = null;
  @Output() showAgain = new EventEmitter<void>();

  private previousLangClass: string | null = null;
  private _isInitializing: boolean = true;

  private localTranslations: any = null;

  constructor(
    private translate: TranslateService,
    private renderer: Renderer2,
    private elRef: ElementRef
  ) {
    }

  ngOnInit(): void {
    // Ensure we load translations for the initial language even if the parent
    // doesn't explicitly change the `lang` input. This prevents hasLocal ===
    // false lookups in translateKeyLocal when the component is first shown.
    try {
      // Load translations WITHOUT changing global language
      this.translate.getTranslation(this.lang).subscribe(
        (trans) => {
          this.localTranslations = trans || {};
          // Force change detection after translations are loaded to ensure correct language display
          setTimeout(() => {
            this.updateBodyClass();
          }, 0);
        },
        (err) => {
          console.error(
            `❌ [TEMPLATE2] getTranslation failed in ngOnInit for "${this.lang}":`,
            err
          );
          this.localTranslations = null;
        }
      );
    } catch (e) {
      console.error(
        `💥 [TEMPLATE2] Error loading translations for "${this.lang}":`,
        e
      );
      this.localTranslations = null;
    }
  }

  ngAfterViewInit(): void {
    this.updateBodyClass();
    this.logDimensions();

    // Load translations for the initial language
    this.loadTranslations();

    // Reset initialization flag after view is fully loaded
    setTimeout(() => {
      this._isInitializing = false;
      this.logDimensions();
    }, 1000); // Wait 1 second to ensure everything is stable
  }

  private loadTranslations(): void {
    try {
      // Load translations WITHOUT changing global language
      this.translate.getTranslation(this.lang).subscribe(
        (trans) => {
          this.localTranslations = trans || {};
          },
        (err) => {
          console.warn("🔵 [TEMPLATE2] Failed to load translations:", err);
          this.localTranslations = null;
        }
      );
    } catch (e) {
      console.warn("🔵 [TEMPLATE2] Error in loadTranslations:", e);
      this.localTranslations = null;
    }
  }

  private logDimensions(): void {
    const hostElement = this.elRef.nativeElement as HTMLElement;
    const pageElement = hostElement.querySelector(".page") as HTMLElement;

}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["lang"]) {
      // Skip all language changes during initialization to prevent Arabic → Hebrew flash
      if (this._isInitializing) {
        return;
      }

      // Only update after initialization is complete
      this.updateBodyClass();
      this.loadTranslations();
    }
  }

  private updateBodyClass(): void {
    const newClass = `lang-${this.lang}`;
    const root =
      (document.querySelector("app-invoice-template2") as HTMLElement) || null;
    if (!root) return;
    if (this.previousLangClass) {
      this.renderer.removeClass(root, this.previousLangClass);
    }
    this.renderer.addClass(root, newClass);
    this.previousLangClass = newClass;
  }

  get direction(): "ltr" | "rtl" {
    return ["ar", "he"].includes(this.lang) ? "rtl" : "ltr";
  }

  getInvoiceTypeName(): string {
    const currentType = AVAILABLEDOCUMENTTYPES.find(
      (type) => +type.id === +this.invoiceData?.invoice_type
    );
    if (!currentType) return "";
    return (
      currentType[`name_${this.lang}` as "name_en" | "name_ar" | "name_he"] ||
      currentType.name_en
    );
  }

  /**
   * Check if the current document is a receipt
   * Receipt types: 400 (Receipt) and 430 (Cancelled Receipt)
   */
  isReceipt(): boolean {
    const invoiceType = String(this.invoiceData?.invoice_type || '');
    return invoiceType === '400' || invoiceType === '430';
  }

  getCompanyName(): string {
    return (
      this.invoiceData?.company?.[`company_name_${this.lang}`] ||
      this.invoiceData?.company?.company_name_en ||
      this.invoiceData?.company_name ||
      ""
    );
  }

  getCustomerName(): string {
    return (
      this.invoiceData?.customer?.[`customer_name_${this.lang}`] ||
      this.invoiceData?.customer?.customer_name_en ||
      this.invoiceData?.customer_name ||
      ""
    );
  }

  getCompanyVat(): string {
    return (
      this.invoiceData?.company?.tax_register ||
      this.invoiceData?.company?.vat ||
      this.invoiceData?.company_vat ||
      this.invoiceData?.tax_register ||
      ""
    );
  }

  getCustomerVat(): string {
    return (
      this.invoiceData?.customer?.customer_vat_number ||
      this.invoiceData?.customer_vat_number ||
      ""
    );
  }

  getCompanyPhone(): string {
    return (
      this.invoiceData?.company?.company_phone ||
      this.invoiceData?.company_phone ||
      ""
    );
  }

  getCompanyAddress(): string {
    const addressKey = `company_address_${this.lang}`;
    const address =
      this.invoiceData?.company?.[addressKey] ||
      this.invoiceData?.[addressKey] ||
      this.invoiceData?.company?.address ||
      this.invoiceData?.company?.company_address ||
      this.invoiceData?.company_address ||
      "";

    // Debug logging to see what address data is available
    return address;
  }

  // Hebrew VAT/ID number formatting logic
  hebVatIdLineForCompany(): string[] {
    const vat = (
      this.invoiceData?.company?.tax_register ||
      this.invoiceData?.company?.vat ||
      ""
    )
      .toString()
      .trim();
    const cid = (
      this.invoiceData?.company?.company_id ||
      this.invoiceData?.company?.id_number ||
      ""
    )
      .toString()
      .trim();

    if (!vat && !cid) return [];
    
    const companyTypePrefix = this.getCompanyTypeInParentheses();
    // Use Hebrew translation for prefix if available, otherwise fallback
    const prefix = companyTypePrefix || this.translateKeyLocal('vatNumber') || 'מספר עוסק';
    
    if (vat && cid && vat !== cid) {
      // Different numbers ⇒ two lines
      return [`${prefix} ${vat}`];
    }
    // Same or only one exists ⇒ one line
    const one = vat || cid;
    return [`${prefix} ${one}`];
  }

  // Get company type in parentheses format based on language
  getCompanyTypeInParentheses(): string {
    const companyType = this.invoiceData?.company?.companyType;
    if (!companyType?.type_name_ar) return '';
    
    const typeNameAr = companyType.type_name_ar.trim();
    
    // Map Arabic type names to translation keys
    const typeToTranslationKey: { [key: string]: string } = {
      'شركة': 'sellerId_Company',
      'مشتغل مرخص': 'sellerId_LicensedDealer',
      'مشتغل معفى': 'sellerId_ExemptDealer',
      'مشتغل معفي': 'sellerId_ExemptDealer', // Handle typo variation
      'جمعيه': 'sellerId_NonProfit',
      'جمعية': 'sellerId_NonProfit'
    };
    
    // Get translation key for this company type
    const translationKey = typeToTranslationKey[typeNameAr];
    if (translationKey) {
      // Try to get translation from local translations first
      const translation = this.translateKeyLocal(translationKey);
      if (translation && translation !== translationKey) {
        return translation;
      }
    }
    
    // Fallback: if translation not found, return Arabic text only for Arabic language
    // For other languages, return empty string to avoid showing Arabic in wrong language
    return this.lang === 'ar' ? typeNameAr : '';
  }

  // Arabic VAT/ID number formatting logic
  arabicVatIdLineForCompany(): string[] {
    const vat = (
      this.invoiceData?.company?.tax_register ||
      this.invoiceData?.company?.vat ||
      ""
    )
      .toString()
      .trim();
    const cid = (
      this.invoiceData?.company?.company_id ||
      this.invoiceData?.company?.id_number ||
      ""
    )
      .toString()
      .trim();

    if (!vat && !cid) return [];
    
    const companyTypePrefix = this.getCompanyTypeInParentheses();
    // Use Arabic translation for prefix if available, otherwise fallback
    const prefix = companyTypePrefix || this.translateKeyLocal('vatNumber') || 'مشتغل رقم';
    
    if (vat && cid && vat !== cid) {
      // Different numbers ⇒ two lines
      return [`${prefix} ${vat}`];
    }
    // Same or only one exists ⇒ one line
    const one = vat || cid;
    return [`${prefix} ${one}`];
  }

  // Get formatted VAT/ID lines based on language
  getFormattedVatIdLines(): string[] {
    if (this.lang === "he") {
      return this.hebVatIdLineForCompany();
    } else if (this.lang === "ar") {
      return this.arabicVatIdLineForCompany();
    } else {
      // English fallback
      const vat = (
        this.invoiceData?.company?.tax_register ||
        this.invoiceData?.company?.vat ||
        ""
      )
        .toString()
        .trim();
      const cid = (
        this.invoiceData?.company?.company_id ||
        this.invoiceData?.company?.id_number ||
        ""
      )
        .toString()
        .trim();

      if (!vat && !cid) return [];
      
      const companyTypePrefix = this.getCompanyTypeInParentheses();
      const prefix = companyTypePrefix || 'VAT Number';
      
      if (vat && cid && vat !== cid) {
        return [`${prefix} ${vat}`];
      }
      const one = vat || cid;
      return [`${prefix} ${one}`];
    }
  }

  getCustomerEmail(): string {
    return this.invoiceData?.customer?.email || "";
  }

  // Complex customer VAT/ID display logic - matches Handlebars template exactly
  getCustomerVatIdLines(): string[] {
    const customerVatNumber =
      this.invoiceData?.customer?.customer_vat_number ||
      this.invoiceData?.customer_vat_number ||
      "";
    const customerIdNumber =
      this.invoiceData?.customer?.customer_id_number ||
      this.invoiceData?.customer_id_number ||
      "";
    const customerType =
      this.invoiceData?.customer?.customer_type ||
      this.invoiceData?.customer_type ||
      6;

    const lines: string[] = [];

    // {{#if data.customer_vat_number}}
    if (customerVatNumber) {
      // {{#ifNotEquals data.customer_vat_number '0'}}
      if (customerVatNumber !== "0") {
        // {{#if data.customer_id_number}}
        if (customerIdNumber) {
          // {{#ifNotEquals data.customer_vat_number data.customer_id_number}}
          if (customerVatNumber !== customerIdNumber) {
            // Different numbers - show both
            // {{#ifEquals data.customer_type 5}}
            if (customerType === 5) {
              // Individual customer
              lines.push(`${this.getCustomerIdLabel()} ${customerIdNumber}`);
              lines.push(
                `${this.getBusinessLicenseLabel()} ${customerVatNumber}`
              );
            } else {
              // Company customer
              lines.push(`${this.getCompanyNumberLabel()} ${customerIdNumber}`);
              lines.push(
                `${this.getBusinessLicenseLabel()} ${customerVatNumber}`
              );
            }
          } else {
            // Same numbers
            // {{#ifEquals data.customer_type 5}}
            if (customerType === 5) {
              // Individual customer - same numbers
              lines.push(
                `${this.getCustomerIdBusinessLabel()} ${customerIdNumber}`
              );
            } else {
              // Company customer - same numbers
              lines.push(
                `${this.getCompanyBusinessLabel()} ${customerIdNumber}`
              );
            }
          }
        } else {
          // No customer_id_number - show VAT only (company format)
          lines.push(`${this.getCompanyBusinessLabel()} ${customerVatNumber}`);
        }
      }
    } else {
      // No customer_vat_number or it's '0' - show ID only if exists
      // {{#if data.customer_id_number}}
      if (customerIdNumber) {
        lines.push(`${this.getCustomerIdLabel()} ${customerIdNumber}`);
      }
    }

    return lines;
  }

  // Helper methods for labels based on language
  private getCustomerIdLabel(): string {
    if (this.lang === "en") return "ID Number";
    if (this.lang === "ar") return "رقم الهوية";
    return "ת.ז.";
  }

  private getCompanyNumberLabel(): string {
    if (this.lang === "en") return "Company Number";
    if (this.lang === "ar") return "رقم الشركة";
    return "ח.פ.";
  }

  private getBusinessLicenseLabel(): string {
    if (this.lang === "en") return "Business License";
    if (this.lang === "ar") return "مشتغل مرخص";
    return "עוסק מורשה";
  }

  private getCustomerIdBusinessLabel(): string {
    if (this.lang === "en") return "ID / Licensed Business";
    if (this.lang === "ar") return "هوية رقم / مشتغل مرخص";
    return "ת.ז. / עוסק מורשה";
  }

  private getCompanyBusinessLabel(): string {
    if (this.lang === "en") return "Company / Licensed Business";
    if (this.lang === "ar") return "شركة رقم / مشتغل مرخص";
    return "ח.פ. / עוסק מורשה";
  }

  getFormattedDate(): string {
    if (!this.invoiceData?.invoice_date) return "";
    const date = new Date(this.invoiceData.invoice_date);
    return date.toLocaleDateString("en-GB");
  }

  formatNumber(value: any): string {
    if (value == null || value === "") return "0.00";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  getConfirmationNumber(): { remainingDigits: string; last9Digits: string } {
    const confirmNumber = this.invoiceData?.confirmation_number || "";
    if (confirmNumber.length <= 9) {
      return { remainingDigits: "", last9Digits: confirmNumber };
    }
    return {
      remainingDigits: confirmNumber.slice(0, -9),
      last9Digits: confirmNumber.slice(-9),
    };
  }

  getPaymentMethodName(type: number): string {
    // Handle deduction type (5) separately
    if (type === 5) {
      if (this.lang === 'en') return 'Deduction';
      if (this.lang === 'ar') return 'خصم';
      return 'ניכוי';
    }
    
    const method = PAYMENTMETHODS.find((m) => m.id === type);
    return method
      ? method[`name_${this.lang}` as "name_en" | "name_ar" | "name_he"]
      : "";
  }

  translateKeyLocal(key: string, params?: Record<string, any>): string {
    try {
      // Resolve nested keys like 'documentLines.total' from the localTranslations object
      const resolveFromLocal = (k: string): string | undefined => {
        if (!this.localTranslations) return undefined;
        const parts = k.split(".");
        let cur: any = this.localTranslations;
        for (let i = 0; i < parts.length; i++) {
          if (
            cur &&
            typeof cur === "object" &&
            Object.prototype.hasOwnProperty.call(cur, parts[i])
          ) {
            cur = cur[parts[i]];
          } else {
            return undefined;
          }
        }
        return typeof cur === "string" ? cur : undefined;
      };

      let value = resolveFromLocal(key);
      // If not found as nested key, also try a direct property (backwards compatibility)
      if (
        value === undefined &&
        this.localTranslations &&
        Object.prototype.hasOwnProperty.call(this.localTranslations, key)
      ) {
        value = this.localTranslations[key];
        }

      // If value is an object with language keys, extract the correct language
      if (value && typeof value === "object" && !Array.isArray(value)) {
        const langValue =
          value[this.lang] || value["en"] || value["ar"] || value["he"];
        if (langValue && typeof langValue === "string") {
          value = langValue;
        } else if (langValue) {
          // If langValue exists but is not a string, use it anyway
          value = String(langValue);
        }
      }

      // If local translations are not available yet, DON'T fall back to global translate service
      // This prevents mixed language display during initialization
      if (value === undefined) {
        // Return the key itself or a placeholder to avoid mixed language display
        return key;
      }

      // Debug logging for missing translations
if (!value) return key;

      // simple interpolation for params using {{key}} or {{ key }} syntax
      if (params && typeof value === "string") {
        Object.keys(params).forEach((p) => {
          const re = new RegExp("{{\\s*" + p + "\\s*}}", "g");
          value = value.replace(re, String(params[p]));
        });
      }

      return typeof value === "string" ? value : key;
    } catch (e) {
      console.error(
        `💥 [TEMPLATE2] Error in translateKeyLocal for key "${key}":`,
        e
      );
      // Don't fall back to global translate service to avoid mixed languages
      return key;
    }
  }

  getPaymentAmountIncludingVat(): number {
    // For INVOICES: Use the standard calculation (payment_amount + VAT)
    // Do NOT use receiptsDetails for invoice totals - they are for payment allocation only
    if (!this.isReceipt()) {
      // Fallback to provided value first
      const providedValue = parseFloat(
        this.invoiceData?.payment_amount_including_vat
      );
      if (!isNaN(providedValue) && providedValue > 0) {
        return providedValue;
      }

      // For invoices: Total = payment_amount + VAT
      const paymentAmount = parseFloat(this.invoiceData?.payment_amount) || 0;
      const vatAmount = parseFloat(this.invoiceData?.all_vat_amount) || 0;
      return paymentAmount + vatAmount;
    }

    // For RECEIPTS: Sum up all payment amounts from receiptsDetails (excluding deductions)
    if (this.invoiceData?.receiptsDetails && Array.isArray(this.invoiceData.receiptsDetails) && this.invoiceData.receiptsDetails.length > 0) {
      const totalFromReceipts = this.invoiceData.receiptsDetails.reduce((sum: number, receipt: any) => {
        // Only sum positive payment amounts (exclude deductions which are typically negative or shown separately)
        const paymentType = Number(receipt.payment_type) || 0;
        const amount = parseFloat(receipt.amount) || 0;
        // Include all payment types (cash, check, etc.) but handle deduction separately if needed
        if (paymentType !== 5) {
          return sum + amount;
        }
        return sum;
      }, 0);
      
      // For receipts, ADD deduction instead of subtracting (deduction is additional amount)
      const deduction = this.getDeductionAmount();
      // For receipts: Total = Payments + Deduction
      const finalTotal = totalFromReceipts + deduction;
      if (finalTotal > 0) {
        return finalTotal;
      }
      // If deduction makes total negative or zero, return the payments total before deduction
      if (totalFromReceipts > 0) {
        return totalFromReceipts;
      }
    }

    // Fallback for receipts
    const providedValue = parseFloat(
      this.invoiceData?.payment_amount_including_vat
    );
    if (!isNaN(providedValue) && providedValue > 0) {
      return providedValue;
    }

    // Final fallback
    const paymentAmount = parseFloat(this.invoiceData?.payment_amount) || 0;
    const vatAmount = parseFloat(this.invoiceData?.all_vat_amount) || 0;
    return paymentAmount + vatAmount;
  }

  /**
   * Get deduction amount from receiptsDetails or deduction_from_source
   */
  getDeductionAmount(): number {
    // First check if deduction is in receiptsDetails (payment_type 5)
    if (this.invoiceData?.receiptsDetails && Array.isArray(this.invoiceData.receiptsDetails)) {
      const deductionFromReceipts = this.invoiceData.receiptsDetails.reduce((sum: number, receipt: any) => {
        if (Number(receipt.payment_type) === 5) {
          return sum + (parseFloat(receipt.amount) || 0);
        }
        return sum;
      }, 0);
      
      if (deductionFromReceipts > 0) {
        return deductionFromReceipts;
      }
    }
    
    // Fallback to deduction_from_source field
    const deductionFromSource = parseFloat(this.invoiceData?.deduction_from_source || '0');
    return deductionFromSource || 0;
  }

  /**
   * Get total payments before deduction
   */
  getTotalPaymentsBeforeDeduction(): number {
    if (this.invoiceData?.receiptsDetails && Array.isArray(this.invoiceData.receiptsDetails)) {
      return this.invoiceData.receiptsDetails.reduce((sum: number, receipt: any) => {
        const paymentType = Number(receipt.payment_type) || 0;
        const amount = parseFloat(receipt.amount) || 0;
        // Sum all payment types except deductions (type 5)
        if (paymentType !== 5) {
          return sum + amount;
        }
        return sum;
      }, 0);
    }
    
    // Fallback to cash_amount or total_amount
    const cashAmount = parseFloat(this.invoiceData?.cash_amount || '0');
    const checksAmount = parseFloat(this.invoiceData?.checks_amount || '0');
    const bankAmount = parseFloat(this.invoiceData?.bank_payment_amount || '0');
    const creditAmount = parseFloat(this.invoiceData?.credit_amount || '0');
    
    return cashAmount + checksAmount + bankAmount + creditAmount;
  }

  /**
   * Check if deduction exists in receiptsDetails
   */
  hasDeductionInReceiptsDetails(): boolean {
    if (!this.invoiceData?.receiptsDetails || !Array.isArray(this.invoiceData.receiptsDetails)) {
      return false;
    }
    return this.invoiceData.receiptsDetails.some((receipt: any) => Number(receipt.payment_type) === 5);
  }

  getBankName(bank: any): string {
    if (!bank) return "";
    if (typeof bank === "string") return bank;
    if (typeof bank === "object") {
      return (
        bank.bank_name ||
        bank.name ||
        bank.bank_name_en ||
        bank.bank_name_he ||
        bank.bank_name_ar ||
        ""
      );
    }
    return "";
  }

  getVatPercentage(): number {
    const vatPercentage =
      this.invoiceData?.vat_percentage ||
      this.invoiceData?.vat_rate ||
      this.invoiceData?.vatPercentage ||
      this.invoiceData?.lines?.[0]?.vat_rate ||
      this.invoiceData?.soldItems?.[0]?.vat_rate ||
      0;
    return parseFloat(vatPercentage) || 0;
  }

  // Calculate totals from lines/items if summary fields are missing (for receipts)
  calculateAmountBeforeDiscount(): number {
    if (this.invoiceData?.amount_before_discount || this.invoiceData?.total_amount_before_discount) {
      return parseFloat(this.invoiceData?.amount_before_discount || this.invoiceData?.total_amount_before_discount) || 0;
    }
    // Calculate from lines if available
    if (this.invoiceData?.lines || this.invoiceData?.soldItems) {
      const items = this.invoiceData?.lines || this.invoiceData?.soldItems || [];
      return items.reduce((sum: number, item: any) => {
        const quantity = parseFloat(item.quantity) || 1;
        const price = parseFloat(item.price_per_unit || item.price) || 0;
        return sum + (price * quantity);
      }, 0);
    }
    // For receipts, if only payment is available, use that as gross total
    const totalFromReceipts = this.getPaymentAmountIncludingVat();
    return totalFromReceipts;
  }

  calculateTotalDiscounts(): number {
    if (this.invoiceData?.all_discount) {
      return parseFloat(this.invoiceData.all_discount) || 0;
    }
    // Calculate from lines if available
    if (this.invoiceData?.lines || this.invoiceData?.soldItems) {
      const items = this.invoiceData?.lines || this.invoiceData?.soldItems || [];
      return items.reduce((sum: number, item: any) => {
        const quantity = parseFloat(item.quantity) || 1;
        const discount = parseFloat(item.discount) || 0;
        return sum + (discount * quantity);
      }, 0);
    }
    return 0;
  }

  calculateTaxableValue(): number {
    if (this.invoiceData?.payment_amount || this.invoiceData?.sub_total) {
      return parseFloat(this.invoiceData?.payment_amount || this.invoiceData?.sub_total) || 0;
    }
    // For receipts without item lines, taxable value equals total amount (if no VAT) or calculated
    const totalAmount = this.getPaymentAmountIncludingVat();
    const vatPercentage = this.getVatPercentage();
    
    // If VAT is 0, taxable value equals total amount
    if (vatPercentage === 0) {
      return totalAmount;
    }
    
    // If VAT > 0, calculate taxable value from total (assuming VAT is included)
    if (vatPercentage > 0 && totalAmount > 0) {
      // totalAmount = taxableValue * (1 + vatPercentage/100)
      // taxableValue = totalAmount / (1 + vatPercentage/100)
      return totalAmount / (1 + (vatPercentage / 100));
    }
    
    // Calculate from lines if available
    if (this.invoiceData?.lines || this.invoiceData?.soldItems) {
      const items = this.invoiceData?.lines || this.invoiceData?.soldItems || [];
      return items.reduce((sum: number, item: any) => {
        const quantity = parseFloat(item.quantity) || 1;
        const subTotal = parseFloat(item.sub_total || item.total) || 0;
        const discount = parseFloat(item.discount) || 0;
        // taxable = subtotal - discount (if VAT included in subtotal)
        // or just subtotal if VAT is separate
        return sum + ((subTotal - discount) * quantity);
      }, 0);
    }
    
    return totalAmount;
  }

  calculateVatAmount(): number {
    if (this.invoiceData?.all_vat_amount) {
      return parseFloat(this.invoiceData.all_vat_amount) || 0;
    }
    
    const vatPercentage = this.getVatPercentage();
    if (vatPercentage === 0) {
      return 0;
    }
    
    // Calculate from lines if available
    if (this.invoiceData?.lines || this.invoiceData?.soldItems) {
      const items = this.invoiceData?.lines || this.invoiceData?.soldItems || [];
      return items.reduce((sum: number, item: any) => {
        const quantity = parseFloat(item.quantity) || 1;
        const vatRate = parseFloat(item.vat_rate || item.vat_percentage) || 0;
        const subTotal = parseFloat(item.sub_total || item.total) || 0;
        if (vatRate > 0 && subTotal > 0) {
          // VAT included in subtotal, calculate backwards
          const net = subTotal / (1 + (vatRate / 100));
          const vat = subTotal - net;
          return sum + (vat * quantity);
        }
        return sum;
      }, 0);
    }
    
    // Calculate from total amount and VAT percentage
    const totalAmount = this.getPaymentAmountIncludingVat();
    const taxableValue = this.calculateTaxableValue();
    if (vatPercentage > 0 && totalAmount > 0) {
      return totalAmount - taxableValue;
    }
    
    return 0;
  }

  hasLogo(): boolean {
    return !!(
      this.invoiceData?.logo_base64 || this.invoiceData?.company?.logo_url
    );
  }

  printPdfLink(): void {
    if (this.pdfLink) {
      window.open(this.pdfLink, "_blank");
    }
  }

  showAgainInvoiceStop(): void {
    this.showAgain.emit();
  }

  /**
   * Helper method to get VAT toggle display text
   */
  getVatToggleText(): string {
    return this.translateKeyLocal("documentLines.incVat") || "VAT Included";
  }

  /**
   * Helper method to get discount type display text
   */
  getDiscountTypeText(): string {
    return (
      this.translateKeyLocal("documentLines.discountTypePercentage") ||
      "Percentage"
    );
  }

  /**
   * Get company type display text based on type_name_ar from API response
   */
  getCompanyTypeDisplayText(): string {
    const typeNameAr = this.invoiceData?.company?.companyType?.type_name_ar;
    if (!typeNameAr) {
      return ""; // Return empty string if no company type data
    }

    // Mapping table for company type display text
    const companyTypeMapping: { [key: string]: string } = {
      "جمعيه": "עמותה / מלכ\"ר (جمعية)",
      "شركة": "חברה (شركة)",
      "مشتغل مرخص": "עוסק מורשה (مشتغل مرخص)",
      "مشتغل معفي": "עוסק פטור (مشتغل معفي)"
    };

    const result = companyTypeMapping[typeNameAr] || typeNameAr;
    return result;
  }
}
